// ============================================
// Identidade visual do RAIZ — verde raiz + Inter.
// Dois temas (claro/escuro) a partir dos mesmos tokens de marca.
// Escala tipográfica de produto (ratio ~1.2), secondary ≠ warning.
//
// FAMÍLIAS DE COR POR SIGNIFICADO (revisão UX out/2026; docs/design-system.md)
// Uma cor semântica carrega UM tipo de notícia, para o olho só correr ao
// vermelho quando o produto está em risco:
//   error    produto em risco — excursão crítica/alta, sensor calado (offline,
//            sem reportar). Calibração NUNCA entra aqui (ver warning/info).
//   warning  atenção sem perda ainda — atrasado, fora da faixa recente,
//            excursão média, calibração vencida, bateria baixa.
//   info     tarefa administrativa / em andamento — calibração pendente ou a
//            vencer, em investigação, monitorando, configuração.
//   success  dentro do esperado — ao vivo, na faixa, justificada/encerrada.
//   default  neutro — nunca falou, rejeitada, sem calibração cadastrada.
// Quem monta chip/KPI consulta raizFormat (presença, severidade, status) e
// CalibrationChip — não escolhe cor à mão.
// ============================================

import { createTheme, type Theme } from '@mui/material/styles'
import { DialogTransition } from './components/DialogTransition'

export type ColorMode = 'light' | 'dark'

// Cor sólida do AppBar por tema — também vira o <meta name="theme-color">
// (barra de status do celular/PWA funde com o app, como app nativo).
export const APPBAR_BG: Record<ColorMode, string> = { light: '#0B3D28', dark: '#0A2A1E' }

// Breakpoint "celular" do MUI (down('sm')) em media query pura, para os
// styleOverrides estáticos do tema.
const MOBILE_MQ = '@media (max-width:599.95px)'

// Tokens de marca compartilhados entre os dois temas.
// Contraste WCAG 2.1 AA: todo `main` do tema claro rende ≥4.5:1 sobre paper
// (#FFFFFF) E sobre default (#F4F7F5) — são usados como texto de chips/labels.
// Validado por src/__tests__/themeContrast.test.ts (trava regressão).
const BRAND = {
  primary: { main: '#0B7A4B', dark: '#075E39', light: '#3D9B70' },
  primaryDark: { main: '#34B37E', dark: '#0F8F5C', light: '#5FC79A' }, // realçado p/ fundo escuro
  secondary: '#2F6FA7', // azul institucional — distinto de warning (âmbar)
  secondaryDark: '#5B9BD5',
  success: '#147A47', // era #188A52 (4.38:1 no paper) — escurecido p/ 5.4:1
  successDark: '#3FB477',
  warning: '#96640A', // era #B7791F (3.64:1 no paper) — escurecido p/ 5.1:1
  warningDark: '#D69B3C',
  error: '#C2453B',
  errorDark: '#E06B5F',
  info: '#2F6FA7',
  infoDark: '#5B9BD5',
}

// Tinta escura p/ texto sobre os `main` claros do tema escuro (chips filled,
// badges): branco não passa de ~3.3:1 sobre eles; esta tinta rende ≥5:1.
const DARK_INK = '#0E1512'

const typography = {
  fontFamily: "'Inter Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  // Escala de produto: contraste por peso, títulos comedidos (h4 ~ 26px).
  h4: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.65rem' },
  h5: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.35rem' },
  h6: { fontWeight: 650, fontSize: '1.1rem' },
  subtitle2: { fontWeight: 650 },
  button: { textTransform: 'none' as const, fontWeight: 600 },
} as const

const componentsFor = (mode: ColorMode) => ({
  // Respeito global a prefers-reduced-motion: clampa animações/transições de
  // TODOS os elementos (inclui ripple, fades de Dialog/Snackbar e keyframes).
  MuiCssBaseline: {
    styleOverrides: {
      // Toque de app nativo: sem flash cinza de tap no WebKit/Android (os
      // controles têm seus próprios estados), sem inflar fonte em landscape,
      // sem rubber-band lateral revelando o fundo do browser.
      html: {
        WebkitTapHighlightColor: 'transparent',
        WebkitTextSizeAdjust: '100%',
        textSizeAdjust: '100%',
      },
      body: {
        overscrollBehaviorX: 'none',
      },
      // No celular, rolagem por toque dispensa trilho visível — as rows
      // roláveis (chips, tabelas) ficam limpas como num app nativo.
      [MOBILE_MQ]: {
        '*': { scrollbarWidth: 'none' },
        '*::-webkit-scrollbar': { width: 0, height: 0, display: 'none' },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important',
        },
      },
    },
  },
  // Foco visível (WCAG 2.4.7) em tudo que é ButtonBase: IconButton, Tab, Chip
  // clicável, CardActionArea, ListItemButton, MenuItem, ToggleButton…
  // currentColor acompanha a cor do texto do controle — visível nos dois temas
  // e também sobre superfícies coloridas (ex.: AppBar verde, texto branco).
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': {
          outline: '2px solid currentColor',
          outlineOffset: 2,
        },
      },
    },
  },
  MuiCard: {
    defaultProps: { variant: 'outlined' as const },
    styleOverrides: { root: { backgroundImage: 'none' } },
  },
  MuiButton: {
    styleOverrides: { root: { borderRadius: 8 } },
  },
  MuiChip: {
    styleOverrides: { root: { fontWeight: 600 } },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: APPBAR_BG[mode],
        backgroundImage:
          mode === 'light'
            ? 'linear-gradient(90deg, #0B3D28 0%, #0B7A4B 130%)'
            : 'linear-gradient(90deg, #0A2A1E 0%, #0F5738 130%)',
        // PWA/notch: a barra pinta a safe-area superior (viewport-fit=cover).
        paddingTop: 'env(safe-area-inset-top)',
      },
    },
  },
  // No celular todo dialog vira bottom sheet: colado embaixo, cantos superiores
  // arredondados, alça de arraste e respiro para a safe-area. A transição de
  // subida correspondente é a DialogTransition (defaultProps abaixo).
  MuiDialog: {
    defaultProps: { TransitionComponent: DialogTransition },
    styleOverrides: {
      container: {
        [MOBILE_MQ]: { alignItems: 'flex-end' },
      },
      paper: {
        [MOBILE_MQ]: {
          '&:not(.MuiDialog-paperFullScreen)': {
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            borderRadius: '20px 20px 0 0',
            maxHeight: 'calc(100dvh - 32px)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            '&::before': {
              content: '""',
              display: 'block',
              flexShrink: 0,
              width: 36,
              height: 4,
              borderRadius: 2,
              margin: '10px auto 2px',
              backgroundColor: mode === 'light' ? 'rgba(27,43,35,0.22)' : 'rgba(232,239,233,0.28)',
            },
          },
        },
      },
    },
  },
})

const lightPalette = {
  mode: 'light' as const,
  primary: BRAND.primary,
  secondary: { main: BRAND.secondary },
  success: { main: BRAND.success },
  warning: { main: BRAND.warning },
  error: { main: BRAND.error },
  info: { main: BRAND.info },
  background: { default: '#F4F7F5', paper: '#FFFFFF' },
  text: { primary: '#1B2B23', secondary: '#5B6E63' },
  divider: 'rgba(27, 43, 35, 0.12)',
}

const darkPalette = {
  mode: 'dark' as const,
  // contrastText explícito: o auto-cálculo do MUI (threshold 3:1) escolheria
  // branco sobre o error claro (3.26:1 — reprova AA); a tinta escura rende ≥5:1
  // sobre todos os `main` do tema escuro.
  primary: { ...BRAND.primaryDark, contrastText: DARK_INK },
  secondary: { main: BRAND.secondaryDark, contrastText: DARK_INK },
  success: { main: BRAND.successDark, contrastText: DARK_INK },
  warning: { main: BRAND.warningDark, contrastText: DARK_INK },
  error: { main: BRAND.errorDark, contrastText: DARK_INK },
  info: { main: BRAND.infoDark, contrastText: DARK_INK },
  // Fundo verde-petróleo bem escuro (instrumento, não neon).
  background: { default: '#0E1512', paper: '#161F1A' },
  text: { primary: '#E8EFE9', secondary: '#9BB0A3' },
  divider: 'rgba(232, 239, 233, 0.14)',
}

export const makeTheme = (mode: ColorMode): Theme =>
  createTheme({
    palette: mode === 'light' ? lightPalette : darkPalette,
    shape: { borderRadius: 10 },
    typography,
    components: componentsFor(mode),
  })

// Compat: alguns módulos/testes importam `theme` diretamente (tema claro).
export const theme = makeTheme('light')
